import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, CheckCircle, UserCheck, X, Trash2, Loader } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';

const WelcomeModal = ({ show, userName, onClose, destination = '/onboarding?step=2', userId }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  if (!show) return null;

  const handleCreateProfile = () => {
    onClose();
    navigate(destination);
  };

  const handleDeleteAccount = async () => {
    if (!userId) {
      toast.error('Unable to delete account. Please try again.');
      return;
    }
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('shootzu_token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/account/${userId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      localStorage.clear();
      toast.success('Account deleted successfully.');
      navigate('/login');
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(8,11,20,.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: 20 }}
    >
      <div
        className="w-full max-w-md animate-fade-in relative"
        style={{
          background: '#12162A',
          border: '1px solid rgba(255,255,255,.12)',
          borderRadius: 22,
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
          fontFamily: "'Work Sans', sans-serif",
        }}
      >
        {/* Close button */}
        <button
          onClick={handleCreateProfile}
          className="absolute top-4 right-4 flex items-center justify-center transition-colors z-20"
          style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.12)', color: '#fff' }}
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div
          className="px-6 py-7 text-center relative"
          style={{
            background: 'linear-gradient(120deg,#572B7C,#78287C)',
            borderTopLeftRadius: 22,
            borderTopRightRadius: 22,
            overflow: 'hidden',
          }}
        >
          <img
            src="/assets/website/symbol-white.svg"
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', right: -28, top: -28, width: 150, height: 150, opacity: 0.06, animation: 'szspin 80s linear infinite', pointerEvents: 'none' }}
          />
          <div
            className="flex items-center justify-center mx-auto mb-4 relative"
            style={{ width: 64, height: 64, borderRadius: '9999px', background: 'rgba(255,255,255,.18)' }}
          >
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white relative" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Welcome to Shootzu{userName ? `, ${userName}` : ''}!
          </h3>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="text-center">
            <p className="text-lg font-semibold mb-4" style={{ color: '#fff' }}>
              For full access to the Shootzu Platform, you must submit a profile for approval.
            </p>

            <p className="mb-4" style={{ color: 'rgba(255,255,255,.6)' }}>
              Take a few minutes to set yourself up for success by creating a great profile to showcase your work.
            </p>
          </div>

          {/* What You Need */}
          <div
            className="p-4"
            style={{ borderRadius: 12, background: 'rgba(40,192,208,.08)', border: '1px solid rgba(40,192,208,.28)' }}
          >
            <div className="flex items-start">
              <UserCheck className="w-5 h-5 mr-3 flex-shrink-0 mt-1" style={{ color: '#28C0D0' }} />
              <div>
                <h4 className="font-medium mb-2" style={{ color: '#fff' }}>Complete your profile with:</h4>
                <ul className="text-sm space-y-1" style={{ color: 'rgba(255,255,255,.6)' }}>
                  <li>• Your skills and experience</li>
                  <li>• Portfolio images showcasing your best work</li>
                  <li>• Business information (ABN, address)</li>
                  <li>• Contact details</li>
                </ul>
              </div>
            </div>
          </div>

          <p className="text-sm text-center" style={{ color: 'rgba(255,255,255,.4)' }}>
            Once submitted, our team will review your profile and activate your account within 24 hours.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 space-y-3">
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCreateProfile();
            }}
            className="w-full font-semibold py-3 cursor-pointer hover:opacity-90"
            style={{ background: '#28C0D0', color: '#06121A' }}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Let's do it! 🚀
          </Button>
          <p className="text-xs text-center" style={{ color: 'rgba(255,255,255,.4)' }}>
            This message will appear every time you visit the dashboard until you complete your profile
          </p>

          {userId && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,.12)' }}>
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full text-xs transition-colors hover:text-red-400"
                  style={{ color: 'rgba(255,255,255,.4)' }}
                >
                  Delete my account
                </button>
              ) : (
                <div className="p-3" style={{ borderRadius: 12, background: 'rgba(185,28,28,.16)', border: '1px solid rgba(185,28,28,.4)' }}>
                  <p className="text-xs mb-3" style={{ color: '#F87171' }}>
                    Are you sure? This will permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                      className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-white disabled:opacity-50 py-2 transition-colors"
                      style={{ background: '#B91C1C', borderRadius: 10 }}
                    >
                      {isDeleting ? (
                        <><Loader size={12} className="animate-spin" /> Deleting...</>
                      ) : (
                        <><Trash2 size={12} /> Yes, delete my account</>
                      )}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className="flex-1 text-xs font-medium py-2 transition-colors"
                      style={{ color: '#fff', background: 'transparent', border: '1px solid rgba(255,255,255,.14)', borderRadius: 10 }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
